import { useOrderbook } from "#/services/kyan/hooks/useOrderbook";
import { useComboInstruments } from "#/store/combo/comboInstruments";
import { type PortfolioGreeks } from "#/types/accounts";
import { type OptionsOrderbook } from "#/types/api";
import { createOptionInstrumentName } from "#/utils/instrument.utils";
import { isBuy } from "#/utils/marketActionConverter";

export const useComboGreeksTableController = () => {
  const { comboOptionInstruments, comboPerpInstruments } =
    useComboInstruments();

  const names: string[] = [];
  const nameToInstrument = new Map<
    string,
    (typeof comboOptionInstruments)[number]
  >();

  comboOptionInstruments.forEach((instrument) => {
    const name = createOptionInstrumentName({
      optionType: instrument.optionType,
      underlying: instrument.underlying,
      strike: instrument.strike,
      maturity: instrument.maturity,
    });
    names.push(name);
    nameToInstrument.set(name, instrument);
  });

  const instrumentNames = names;

  const { data: orderbook } = useOrderbook(instrumentNames);

  const calculateGreekTotal = (
    greekProperty: keyof PortfolioGreeks,
  ): number => {
    return orderbook.reduce((acc, curr) => {
      const current = curr as OptionsOrderbook;
      const greekValue = current.greeks[greekProperty];

      const instrument = nameToInstrument.get(current.instrument_name);

      if (!instrument) return acc;

      const multiplier = isBuy(instrument.side) ? 1 : -1;

      const adjustedValue = greekValue * Number(instrument.size) * multiplier;

      return acc + adjustedValue;
    }, 0);
  };

  const delta = calculateGreekTotal("delta");
  const gamma = calculateGreekTotal("gamma");
  const vega = calculateGreekTotal("vega");
  const theta = calculateGreekTotal("theta");

  const { size, side } = comboPerpInstruments.at(0) || {
    size: "",
    side: "buy",
  };
  const perpDelta = Number(size) * (isBuy(side) ? 1 : -1);

  return {
    delta,
    gamma,
    vega,
    theta,
    perpDelta,
  };
};
